<template>
  <div class="apply-form__section apply-form__previous-jobs">
    <h2>{{ $t('application.previousJobs.title') }}</h2>

    <div class="d-none d-md-block">
      <v-simple-table class="previous-jobs-table rounded-lg">
        <thead>
          <tr>
            <th>{{ $t('application.previousJobs.occupation') }}</th>
            <th>{{ $t('application.previousJobs.supervisor') }}</th>
            <th>{{ $t('application.previousJobs.department') }}</th>
            <th>{{ $t('application.previousJobs.from') }}</th>
            <th>{{ $t('application.previousJobs.until') }}</th>
            <th class="text-right"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="jobs.length === 0">
            <td class="text--muted" colspan="6">
              {{ $t('application.previousJobs.empty') }}
            </td>
          </tr>
          <tr v-for="(job, index) in jobs" :key="`job_row_${index}`">
            <td>{{ job.Job }}</td>
            <td>{{ job.Supervisor }}</td>
            <td>{{ job.Department }}</td>
            <td>{{ job.From }}</td>
            <td>{{ job.To }}</td>
            <td class="text-right">
              <v-btn icon small @click="editJob(index)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small @click="removeJob(index)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <div class="d-block d-md-none">
      <v-card
        v-for="(job, index) in jobs"
        :key="`job_card_${index}`"
        class="mb-3"
        outlined
      >
        <v-card-title class="text-body-1">
          {{ job.Job }}
        </v-card-title>
        <v-card-text class="pt-0">
          <div>
            <strong>{{ $t('application.previousJobs.supervisor') }}:</strong>
            {{ job.Supervisor }}
          </div>
          <div>
            <strong>{{ $t('application.previousJobs.department') }}:</strong>
            {{ job.Department }}
          </div>
          <div>
            <strong>{{ $t('application.previousJobs.from') }}:</strong>
            {{ job.From }}
          </div>
          <div>
            <strong>{{ $t('application.previousJobs.until') }}:</strong>
            {{ job.To }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="editJob(index)">
            {{ $t('application.previousJobs.edit') }}
          </v-btn>
          <v-btn text small @click="removeJob(index)">
            {{ $t('application.previousJobs.remove') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <div v-if="jobs.length === 0" class="text--muted mb-3">
        {{ $t('application.previousJobs.empty') }}
      </div>
    </div>

    <div class="text-right mt-4">
      <v-btn color="primary" outlined small @click="openAddDialog">
        <v-icon left>mdi-plus</v-icon>
        {{ $t('application.previousJobs.add') }}
      </v-btn>
    </div>

    <v-dialog v-model="dialog" persistent max-width="640">
      <v-card class="has--title">
        <v-card-title>
          {{
            editedIndex < 0
              ? $t('application.previousJobs.add')
              : $t('application.previousJobs.edit')
          }}
        </v-card-title>
        <v-card-text>
          <div class="d-block">
            <v-form ref="form" v-model="formValid">
              <v-text-field
                v-model="editedItem.Job"
                filled
                outlined
                flat
                hide-details="auto"
                class="mb-2"
                :label="`${$t('application.previousJobs.occupation')}*`"
                :rules="[requiredRule]"
              />
              <v-text-field
                v-model="editedItem.Supervisor"
                filled
                outlined
                flat
                hide-details="auto"
                class="mb-2"
                :label="`${$t('application.previousJobs.supervisor')}*`"
                :rules="[requiredRule]"
              />
              <v-text-field
                v-model="editedItem.Department"
                filled
                outlined
                flat
                hide-details="auto"
                class="mb-2"
                :label="`${$t('application.previousJobs.department')}*`"
                :rules="[requiredRule]"
              />
              <v-menu
                v-model="fromMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="editedItem.From"
                    filled
                    outlined
                    flat
                    hide-details="auto"
                    class="mb-2"
                    :label="`${$t('application.previousJobs.from')}*`"
                    :hint="$t('application.dateFormatInfo')"
                    persistent-hint
                    :rules="[requiredRule, validDateRule]"
                    v-on="on"
                  >
                    <template v-slot:append>
                      <v-btn
                        icon
                        small
                        :aria-label="$t('application.openCalendar')"
                        @click="fromMenu = true"
                      >
                        <v-icon>mdi-calendar</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="fromPicker"
                  class="pa-0"
                  first-day-of-week="1"
                  @input="applyFromDate"
                />
              </v-menu>

              <v-menu
                v-model="toMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="editedItem.To"
                    filled
                    outlined
                    flat
                    hide-details="auto"
                    class="mb-2"
                    :label="$t('application.previousJobs.until')"
                    :hint="$t('application.dateFormatInfo')"
                    persistent-hint
                    :rules="[optionalDateRule]"
                    v-on="on"
                  >
                    <template v-slot:append>
                      <v-btn
                        icon
                        small
                        :aria-label="$t('application.openCalendar')"
                        @click="fromMenu = true"
                      >
                        <v-icon>mdi-calendar</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="toPicker"
                  class="pa-0"
                  first-day-of-week="1"
                  @input="applyToDate"
                />
              </v-menu>
            </v-form>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">{{ $t('close') }}</v-btn>
          <v-btn color="primary" @click="saveJob">
            {{ $t('application.previousJobs.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  data: () => ({
    dialog: false,
    formValid: false,
    editedIndex: -1,
    fromMenu: false,
    toMenu: false,
    fromPicker: null,
    toPicker: null,
    editedItem: {
      Job: '',
      Supervisor: '',
      Department: '',
      From: '',
      To: ''
    },
    defaultItem: {
      Job: '',
      Supervisor: '',
      Department: '',
      From: '',
      To: ''
    }
  }),

  computed: {
    jobs: {
      get() {
        return this.value ?? [];
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    requiredRule() {
      return (value) => {
        if (!value || value.toString().trim() === '') {
          return this.$t('validation.fieldIsMandatory');
        }

        return true;
      };
    },

    validDateRule() {
      return (value) => {
        if (!this.isValidDate(value)) {
          return this.$t('validation.dateInvalid');
        }

        return true;
      };
    },

    optionalDateRule() {
      return (value) => {
        if (!value || value.toString().trim() === '') {
          return true;
        }

        return this.isValidDate(value)
          ? true
          : this.$t('validation.dateInvalid');
      };
    }
  },

  methods: {
    openAddDialog() {
      this.editedIndex = -1;
      this.editedItem = { ...this.defaultItem };
      this.fromPicker = null;
      this.toPicker = null;
      this.dialog = true;
    },

    editJob(index) {
      this.editedIndex = index;
      this.editedItem = { ...this.jobs[index] };
      this.fromPicker = this.toPickerDate(this.editedItem.From);
      this.toPicker = this.toPickerDate(this.editedItem.To);
      this.dialog = true;
    },

    removeJob(index) {
      const updated = [...this.jobs];
      updated.splice(index, 1);
      this.jobs = updated;
    },

    closeDialog() {
      this.dialog = false;
      this.editedItem = { ...this.defaultItem };
      this.editedIndex = -1;
      this.fromMenu = false;
      this.toMenu = false;
      this.fromPicker = null;
      this.toPicker = null;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    saveJob() {
      if (this.$refs.form && !this.$refs.form.validate()) {
        return;
      }

      const job = {
        Job: this.editedItem.Job?.trim() ?? '',
        Supervisor: this.editedItem.Supervisor?.trim() ?? '',
        Department: this.editedItem.Department?.trim() ?? '',
        From: this.editedItem.From?.trim() ?? '',
        To: this.editedItem.To?.trim() ?? ''
      };

      const updated = [...this.jobs];

      if (this.editedIndex < 0) {
        updated.push(job);
      } else {
        updated.splice(this.editedIndex, 1, job);
      }

      this.jobs = updated;
      this.closeDialog();
    },

    applyFromDate(value) {
      this.editedItem.From = this.formatPickerDate(value);
      this.fromMenu = false;
    },

    applyToDate(value) {
      this.editedItem.To = this.formatPickerDate(value);
      this.toMenu = false;
    },

    toPickerDate(formattedDate) {
      if (!formattedDate) {
        return null;
      }

      if (formattedDate.includes('.')) {
        const [day, month, year] = formattedDate.split('.');
        return `${year}-${month}-${day}`;
      }

      return formattedDate;
    },

    formatPickerDate(date) {
      if (!date) {
        return '';
      }

      if (date.includes('-')) {
        const [year, month, day] = date.split('-');
        return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
      }

      return date;
    },

    isValidDate(formattedDate) {
      if (!formattedDate || formattedDate.toString().trim() === '') {
        return false;
      }

      const [day, month, year] = formattedDate.split('.');
      if (day && month && year && year > 1000 && year < 3000) {
        const date = `${year}-${month}-${day}`;
        return !isNaN(new Date(date));
      }

      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
table > tbody > tr:hover {
  background: none !important;
}
</style>
